'use client'

import { cn } from '@/lib/utils'
import React from 'react'

import { ElementType, ForwardedRef, ReactNode } from 'react'

interface ClassName {
  className?: string
}

export interface SectionProps extends ClassName {
  children?: ReactNode
  ref?: ForwardedRef<HTMLElement>
  isFullScreen?: boolean
  backgroundColor?: string
  id?: string
}

export interface SectionTitleProps extends ClassName {
  children: ReactNode
  as?: ElementType
}

export interface SectionHeaderProps extends ClassName {
  children: ReactNode
}

export interface SectionFooterProps extends ClassName {
  children: ReactNode
}

export const Section: React.FC<SectionProps> = ({ children, ref, className, id }) => {
  return (
    <section
      className={cn('mx-auto my-8 box-border w-full max-w-6xl px-6', className)}
      ref={ref}
      id={id}
    >
      {children}
    </section>
  )
}

export function SectionTitle({ children, className, as: Component = 'h2' }: SectionTitleProps) {
  return (
    <Component
      className={cn(
        'font-heading text-3xl leading-none font-extrabold tracking-[0.5px] uppercase md:text-4xl',
        className,
      )}
    >
      {children}
    </Component>
  )
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({ children, className }) => {
  return <div className={cn('mx-auto max-w-2xl text-center', className)}>{children}</div>
}

export const SectionFooter: React.FC<SectionFooterProps> = ({ children, className }) => {
  return <div className={cn('mt-6 text-center md:mt-8', className)}>{children}</div>
}

export const SectionDescription: React.FC<SectionProps> = ({ children, className }) => {
  return (
    <p className={cn('text-muted-foreground font-body text-lg md:mt-1', className)}>{children}</p>
  )
}

export const SectionContent: React.FC<SectionProps> = ({ children, className }) => {
  return <div className={cn('mt-4 w-full md:mt-6', className)}>{children}</div>
}
